// @flow
/** @jsx jsx */

import { createContext, useCallback, useContext, useEffect } from 'react';

import { Link } from '@zapier/common-components';
import { jsx } from '@emotion/core';
import {
  addParamsToUrl,
  extractParams,
  removeParamsFromUrl,
} from '@zapier/common-utils';

import type {
  SSOButtonProps as Props,
  SSOStartUrlParamsContextType,
} from '../../types';

export const SSOStartUrlParamsContext = createContext<SSOStartUrlParamsContextType>(
  {
    build_auths: false,
  }
);

export function getStartUrlForProvider(provider: string) {
  return `/sso/start?provider=${provider}`;
}

const SSOButton = ({
  children,
  href,
  next,
  shouldOpenInChildWindow,
  passThrough,
  onTrackSignupIntent,
  onTrackSignupConversion,
}: Props) => {
  const ssoStartUrlParams = useContext(SSOStartUrlParamsContext);

  const ssoStartUrl = addParamsToUrl(href, {
    ...ssoStartUrlParams,
    in_child_window: shouldOpenInChildWindow,
  });

  const onAuthComplete = useCallback(
    async (event: SyntheticInputEvent<HTMLElement>) => {
      let data = {};
      try {
        data = JSON.parse(event.data);
        if (typeof data === 'string') {
          data = JSON.parse(data);
        }
      } catch (e) {
        return;
      }

      const { nextUrl } = data;
      if (!nextUrl) {
        console.error('Error deciphering next URL from posted window message.');
        return;
      }

      const params = extractParams(nextUrl);
      if (params.new_signup) {
        try {
          await onTrackSignupConversion(params.customuser_id);
        } catch (e) {
          console.error(e);
        }
      }

      const redirectUrl = removeParamsFromUrl(nextUrl, ['customuser_id']);
      window.location = redirectUrl;
    },
    [onTrackSignupConversion]
  );

  const onMessage = useCallback(
    (event: SyntheticInputEvent<HTMLElement>) => {
      // TODO: ignore all other messages but complete
      onAuthComplete(event);
    },
    [onAuthComplete]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);

  const onClick = useCallback(() => {
    if (passThrough && next) {
      window.location.href = next;
    }

    onTrackSignupIntent();

    if (shouldOpenInChildWindow) {
      window.addEventListener('message', onMessage);
      window.open(
        ssoStartUrl,
        'popupwindow',
        'height=600,width=980,resizable=yes,scrollbars=yes'
      );
    } else {
      window.location.href = ssoStartUrl;
    }
  }, [
    next,
    onMessage,
    onTrackSignupIntent,
    passThrough,
    shouldOpenInChildWindow,
    ssoStartUrl,
  ]);

  return (
    <Link onClick={onClick} underline="never">
      {children}
    </Link>
  );
};

SSOButton.defaultProps = {
  passThrough: false,
  shouldOpenInChildWindow: true,
  onTrackSignupIntent() {},
  // eslint-disable-next-line no-unused-vars
  onTrackSignupConversion(userId) {},
};

export default SSOButton;
